import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import { toast } from "sonner";
import { Bot, Send, Loader2, GraduationCap, FlaskConical, AlertTriangle } from "lucide-react";

const MODES = [
  { key: "education", label: "Wellness Educator", icon: GraduationCap,
    intro: "Ask me anything about menopause, hormones, sleep, stress, metabolic health, or general GLP-1 education. I'm here to help you understand — not to diagnose." },
  { key: "lab_explainer", label: "Lab Explainer", icon: FlaskConical,
    intro: "Tell me a lab term or range you'd like explained in plain language. I'll explain what it generally means — your provider interprets your actual results." },
];

export default function AIAssistant() {
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState("education");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  const loadHistory = (m) => api.get(`/ai/history?mode=${m}`).then((r) => setMessages(r.data)).catch(() => {});
  useEffect(() => { loadHistory(mode); }, [mode]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);
  useEffect(() => {
    const q = searchParams.get("q");
    if (q) setInput(q);
  }, [searchParams]);

  const current = MODES.find((m) => m.key === mode);

  const send = async (e) => {
    e.preventDefault();
    if (!input.trim() || busy) return;
    const text = input;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", text, id: `tmp-${Date.now()}` }]);
    setBusy(true);
    try {
      const { data } = await api.post("/ai/chat", { message: text, mode });
      setMessages((prev) => [...prev, { role: "assistant", text: data.reply, id: `a-${Date.now()}` }]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "The assistant is unavailable");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppLayout>
      <div className="max-w-3xl mx-auto flex flex-col h-[calc(100vh-8rem)]">
        <div className="animate-fade-up">
          <span className="text-xs font-medium uppercase tracking-[0.2em] text-[#9B7E8D]">AI Companion</span>
          <h1 className="mt-2 font-serif text-5xl tracking-tight text-wine">Ask BloomHer</h1>
        </div>

        {/* Mode toggle */}
        <div className="mt-6 flex gap-2.5">
          {MODES.map((m) => {
            const Icon = m.icon;
            return (
              <button
                key={m.key}
                data-testid={`ai-mode-${m.key}`}
                onClick={() => setMode(m.key)}
                className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-all ${mode === m.key ? "bg-wine text-white" : "bg-white border border-taupe text-[#6E5862] hover:bg-cream"}`}
              >
                <Icon className="h-4 w-4" strokeWidth={1.5} /> {m.label}
              </button>
            );
          })}
        </div>

        <div className="mt-4 flex items-start gap-2 rounded-2xl bg-[#F7E9EF] px-4 py-3 border border-gold/20">
          <AlertTriangle className="h-4 w-4 text-gold flex-shrink-0 mt-0.5" strokeWidth={1.5} />
          <p className="text-xs text-[#6E5862] leading-relaxed">
            This assistant provides general education only — never diagnosis, prescriptions, or treatment advice. For personal care, please book a consult.
          </p>
        </div>

        {/* Messages */}
        <div className="mt-4 flex-1 overflow-y-auto space-y-4 pr-1">
          {messages.length === 0 && (
            <div className="bg-white rounded-3xl p-8 border border-taupe text-center">
              <Bot className="h-10 w-10 text-wine mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-base text-[#6E5862] leading-relaxed">{current.intro}</p>
            </div>
          )}
          {messages.map((m) => (
            <div key={m.id} data-testid={`ai-msg-${m.role}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-3xl px-5 py-3.5 text-base leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-wine text-white" : "bg-white border border-taupe text-[#3A1230]"}`}>
                {m.text}
              </div>
            </div>
          ))}
          {busy && (
            <div className="flex justify-start">
              <div className="rounded-3xl bg-white border border-taupe px-5 py-4">
                <Loader2 className="h-5 w-5 animate-spin text-wine" strokeWidth={1.5} />
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        {/* Input */}
        <form onSubmit={send} className="mt-4 flex gap-2">
          <input
            data-testid="ai-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={mode === "lab_explainer" ? "e.g. What does TSH mean?" : "Ask about your wellness…"}
            className="flex-1 rounded-full border border-[#D8C2CD] bg-white px-5 py-3.5 text-base text-[#3A1230] placeholder-[#9B7E8D] focus:border-wine focus:outline-none focus:ring-1 focus:ring-wine"
          />
          <button data-testid="ai-send-btn" type="submit" disabled={busy} className="rounded-full bg-wine px-6 text-white hover:bg-wine-dark transition-colors disabled:opacity-60">
            <Send className="h-5 w-5" />
          </button>
        </form>
      </div>
    </AppLayout>
  );
}
